import React, {useEffect, useState} from 'react';
import {
    CreateButton,
    Datagrid,
    DateField,
    downloadCSV,
    ExportButton,
    Filter,
    List,
    Pagination,
    DateInput,
    SelectInput,
    TextField,
    TextInput,
    TopToolbar,
    useListContext,
    FunctionField,
    FilterButton,
    AutocompleteArrayInput
} from 'react-admin';
import TicketOptions from "./TicketOptions";
import jsonExport from 'jsonexport/dist';
import { isinFields } from "../i18n/en";
import ClearFilters from "./ClearFilters";
import { ticketStatus } from "./TicketEnums";
import { priorityRenderer, statusColorRenderer } from "./ColorRenderer";
import { AutocompleteInput } from 'react-admin';

export const convertToTicketTags = (fields: Record<string, string>): { id: string, name: string }[] => {
    return Object.entries(fields)
            .sort(([, valueA], [, valueB]) => valueA.localeCompare(valueB))
            .map(([key, value]) => ({
                id: value,
                name: value
            }));
};

const ticketTags = convertToTicketTags(isinFields);

const validateDateFrom = (value, values) => {
    if (values.date_created_lte && value) {
        if (new Date(value) > new Date(values.date_created_lte)) {
            return 'Date Created From must be earlier than Date Created To';
        }
    }
    return undefined;
};

const validateDateTo = (value, values) => {
    if (values.date_created_gte && value) {
        if (new Date(value) < new Date(values.date_created_gte)) {
            return 'Date Created To must be later than Date Created From';
        }
    }
    return undefined;
};

const validateDateUpdatedFrom = (value, values) => {
    if (values.date_updated_lte && value) {
        if (new Date(value) > new Date(values.date_updated_lte)) {
            return 'Date Updated From must be earlier than Date Updated To';
        }
    }
    return undefined;
};

const validateDateUpdatedTo = (value, values) => {
    if (values.date_updated_gte && value) {
        if (new Date(value) < new Date(values.date_updated_gte)) {
            return 'Date Updated To must be later than Date Updated From';
        }
    }
    return undefined;
};

type IdName = { id: number; name: string };

const useOwnerNnas = () => {
    const [choices, setChoices] = useState<IdName[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch('/anna/api/v1/owner-nnas');
                const data: IdName[] = await res.json();
                if (!cancelled) setChoices(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return { choices, loading };
};

const OwnerNnaFilter: React.FC<any> = (props) => {
    const { choices, loading } = useOwnerNnas();
    return (
            <AutocompleteInput
                    {...props}
                    choices={choices}
                    optionText="name"
                    optionValue="id"
                    loading={loading}
                    className="custom-filter-field"
            />
    );
};


const CustomFilters = [
    <TextInput
            label="Search by words"
            source="title"
            alwaysOn
            multiline
            rowsMax={5}
            parse={value => (value ? value.toUpperCase() : '')}
            className="custom-filter-field"
            key="title"
    />,
    <AutocompleteArrayInput
            label="Search by status"
            source="status"
            choices={ticketStatus}
            optionText="name"
            optionValue="id"
            fullWidth
            alwaysOn
            className="custom-filter-field"
            key="status"
    />,
    <SelectInput
            className="custom-filter-field"
            label="Search by fields"
            source="tags"
            choices={ticketTags}
            alwaysOn
            key="tags"
    />,
    <OwnerNnaFilter
            key="ownerNna"
            label="Owner Group"
            source="ownerCustomerId"
            fullWidth
    />,
    <OwnerNnaFilter
            key="assignedNna"
            label="Assigned Group"
            source="assignedToCustomerId"
            fullWidth
    />,
    <DateInput className="custom-filter-field-date" label="Date Created From" source="date_created_gte" validate={validateDateFrom} key="dc_from" />,
    <DateInput className="custom-filter-field-date" label="Date Created To"   source="date_created_lte" validate={validateDateTo}   key="dc_to" />,
    <DateInput className="custom-filter-field-date" label="Date Updated From" source="date_updated_gte" validate={validateDateUpdatedFrom} key="du_from" />,
    <DateInput className="custom-filter-field-date" label="Date Updated To"   source="date_updated_lte" validate={validateDateUpdatedTo}   key="du_to" />
];

const Tickets: React.FC = (props) => {
    const CustomActions = () => {
        const { basePath } = useListContext();

        return (
                <TopToolbar>
                    <FilterButton filters={CustomFilters} />
                    <CreateButton basePath={basePath} />
                    <TicketOptions />
                    <ClearFilters />
                    <ExportButton basePath={basePath} />
                </TopToolbar>
        );
    };

    const createExcelStringValue = (value : string) : string => {
        return `"${value.replace(/\n/g, " ").replace(/"/g, '""')}"`;
    };

    const exporter = (data: any[], fetchRelatedRecords: () => void) => {
        data = data.map(record => {
            const { username,assignedToUserId,assignedToCustomerId,ownerUserId,ownerCustomerId,commentToAdd,id, ...rest } = record;  // This line excludes the `username` field
            let bodyContent = record.body ? record.body.replace(/\n/g, " ").replace(/\r/g, "").replace(/"/g, '""').replace(";", ",") : "";
            let titleContent = record.title ? record.title.replace(/\n/g, " ").replace(/\r/g, "").replace(/"/g, '""').replace(";", ",") : "";
            return {
                ...rest,
                body: bodyContent,
                title: titleContent,
                tags: record.tags ? record.tags.replace(/\n/g, "|") : "",
                ticketId: record.ticketId ? "\"" + record.ticketId + "\"" : "",
            };
        });

        jsonExport(data, {
            headers: ['date_created', 'date_updated', 'title', 'body', 'ownernnaName','ownerName','assignedTonnaName','assignedToName','status','priority','currentTicketRole','tags','ticketId'],
            rename: ['Created','Updated','Title','Description','Group owner','Owner','Assigned group','Assignee','Status','Priority','Role','Tags','Ticket Id'], delimiter: ','
        }, (err: any, csv: string) => {
            downloadCSV(csv, 'tickets export');
        });
    };

    const getStatusName = (id: string): string => {
        const status = ticketStatus.find(status => status.id === id);
        return status ? status.name : id; // Return the name if found, otherwise return the id
    };

    const StatusField: React.FC<{ record?: any }> = ({ record }) => {
        return (
                <span>{getStatusName(record.status)}</span>
        );
    };

    return (
            <>
                <List exporter={exporter}
                      {...props}
                      pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100, 400]}/>}
                      bulkActionButtons={false}
                      actions={<CustomActions/>}
                      filters={<Filter className="custom-filter-form">{CustomFilters}</Filter>}
                      sort={{ field: 'id', order: 'ASC' }}>
                    {/*<Datagrid data={tasks} ids={tasks.map(item => item.id)} rowClick="edit">*/}
                    <Datagrid rowClick="edit">
                        <TextField label="Ticket Id" source="ticketId"/>
                        <DateField source="date_created" options={{
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                        }}/>
                        <DateField source="date_updated" options={{
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                        }}/>
                        <TextField label="Title" source="title"/>
                        <TextField label="Description" multiline source="body"/>
                        <TextField label="Owner Group" source="ownernnaName"/>
                        <TextField label="Owner" source="ownerName"/>
                        <TextField label="Assigned Group" source="assignedTonnaName"/>
                        <TextField label="Assigned" source="assignedToName"/>
                        <FunctionField label="Status" source="status" sortable={true} sortBy="status"
                                       render={statusColorRenderer}/>
                        <FunctionField label="Priority" source="priority" sortable={true} sortBy="priority"
                                       render={priorityRenderer}/>
                    </Datagrid>
                </List>
            </>
    );
};

export default Tickets;
